import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormRenderer } from '@kevlarcon/formkit';
import PartnerLayout from '../../components/PartnerLayout';
import { formKitClient } from '../../api/formkitClient';
import {
  partnersApi,
  PartnerMine,
  partnerStatusChipClass,
  partnerStatusLabel,
} from '../../api/partners';
import '../../styles/formkitTheme.css';

// What each state of the partner lifecycle (partners/form_types.py's
// STATUS_CHOICES) means to the school itself, not to staff.
const STATUS_BLURB: Record<string, string> = {
  imported: 'Our team added your school. Check the information below and submit it for review.',
  submitted: 'Thanks! Our team is reviewing your listing. You can still make changes while you wait.',
  published: 'Your school is live in the partner directory. Changes you save here go live right away.',
  rejected: "We couldn't publish your listing yet. Our team will reopen it so you can make changes.",
  partnered: 'Your school is a partner. Thank you.',
};

/**
 * A school's own console page (`/partners/dashboard`) — the "maintain my listing" half of
 * the account-first join, a sibling of the admin console's Partners screens
 * rather than a CMS page. `PartnerViewSet.mine` supplies everything: the
 * school's listing, or the one imported row its verified email can claim.
 *
 * While the state machine lets the school edit (`editable`) it gets the same
 * schema-driven form it joined with, pre-filled, via `FormRenderer`'s editor
 * variant — a just-claimed `imported` row opens straight into it, since its
 * first save is what sends the listing to review. Otherwise the profile is a
 * read-only label/value list (`FormRenderer` can't show an existing record's
 * choice fields — see partners/views.py's `_resolve_choice_label`).
 */
const PartnerDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const { data: form, isLoading: formLoading } = useQuery({
    queryKey: ['partner-public-form'],
    queryFn: partnersApi.getPublicForm,
  });
  const formId = form?.id;

  const { data: mine, isLoading: mineLoading, isError } = useQuery<PartnerMine>({
    queryKey: ['partner-mine', formId],
    queryFn: () => partnersApi.mine(formId!),
    enabled: formId != null,
  });

  // A just-claimed `imported` listing goes straight to the form.
  const listingId = mine?.listing?.id;
  const listingStatus = mine?.listing?.status;
  useEffect(() => {
    setEditing(listingStatus === 'imported');
  }, [listingId, listingStatus]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['partner-mine', formId] });

  // `FormRenderer` always saves through the client's `submitSubmission`; point
  // it at `update_mine` (which leaves a live listing live) instead of formkit's
  // stock resubmit — same wrapper trick as AddPartnerModal. Everything else
  // (schema, prefill) still goes through the stock client.
  const client = useMemo(
    () => ({
      ...formKitClient,
      submitSubmission: (fId: number, data: Record<string, unknown>) =>
        partnersApi.updateMine(fId, data),
    }),
    [],
  );

  const claim = useMutation({
    mutationFn: (submissionId: number) => partnersApi.confirmClaim(formId!, submissionId),
    onSuccess: refresh,
  });

  if (formLoading || mineLoading || !mine) {
    return (
      <PartnerLayout title="My listing">
        <p className="sgk-admin__sub" data-testid="partner-dashboard-loading">
          {isError ? 'Something went wrong loading your listing. Please refresh.' : 'Loading…'}
        </p>
      </PartnerLayout>
    );
  }

  const { listing, claimable } = mine;

  if (!listing && claimable) {
    return (
      <PartnerLayout title="Is this your school?">
        <div className="sgk-panel" data-testid="partner-dashboard-claim">
          <h2 className="sgk-panel__title">We found a listing for your email</h2>
          <p className="sgk-panel__hint">
            <strong data-testid="partner-dashboard-claim-name">{claimable.school_name}</strong>
            {(claimable.city || claimable.state) && (
              <> — {[claimable.city, claimable.state].filter(Boolean).join(', ')}</>
            )}
          </p>
          {claim.isError && (
            <p className="sgk-admin__sub" data-testid="partner-dashboard-claim-error">
              We couldn’t claim that listing. Please try again.
            </p>
          )}
          <div className="sgk-btnrow">
            <button
              type="button"
              className="sgk-btn sgk-btn--gold sgk-btn--sm"
              disabled={claim.isPending}
              onClick={() => claim.mutate(claimable.id)}
              data-testid="partner-dashboard-claim-button"
            >
              {claim.isPending ? 'Claiming…' : 'Yes, this is my school'}
            </button>
          </div>
        </div>
      </PartnerLayout>
    );
  }

  if (!listing) {
    return (
      <PartnerLayout title="My listing">
        <div className="sgk-panel" data-testid="partner-dashboard-empty">
          <h2 className="sgk-panel__title">You haven’t added your school yet</h2>
          <p className="sgk-panel__hint">
            Add your school to the partner directory to connect with families.
          </p>
          <div className="sgk-btnrow">
            <Link
              to="/partners/join"
              className="sgk-btn sgk-btn--gold sgk-btn--sm"
              data-testid="partner-dashboard-join-link"
            >
              Add your school
            </Link>
          </div>
        </div>
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout title={listing.school_name || '(untitled)'} subtitle="Your listing" wide>
      <div className="sgk-panel" data-testid="partner-dashboard-status-panel">
        <h2 className="sgk-panel__title">Status</h2>
        <p className="sgk-panel__hint">
          <span className={partnerStatusChipClass(listing.status)} data-testid="partner-dashboard-status-chip">
            {partnerStatusLabel(listing.status)}
          </span>
          {' · '}
          <span data-testid="partner-dashboard-status-blurb">{STATUS_BLURB[listing.status] ?? ''}</span>
        </p>
        {listing.status === 'rejected' && listing.rejection_reason && (
          <p className="sgk-admin__sub" data-testid="partner-dashboard-rejection-reason">
            From our team: {listing.rejection_reason}
          </p>
        )}
        {listing.public_path && (
          <div className="sgk-btnrow">
            <a
              href={listing.public_path}
              className="sgk-btn sgk-btn--ghost sgk-btn--sm"
              data-testid="partner-dashboard-public-link"
            >
              View your public page
            </a>
          </div>
        )}
      </div>

      <div className="sgk-panel">
        <h2 className="sgk-panel__title">Profile</h2>
        {editing && listing.editable ? (
          <div data-testid="partner-dashboard-form">
            <FormRenderer
              client={client}
              formId={formId!}
              submissionId={listing.id}
              variant="editor"
              onSubmitted={() => {
                setEditing(false);
                refresh();
              }}
            />
          </div>
        ) : (
          <>
            <dl className="sgk-proflist" data-testid="partner-dashboard-profile">
              {listing.fields.map((field) => (
                <div className="sgk-proflist__row" key={field.name}>
                  <dt>{field.label}</dt>
                  <dd data-testid={`partner-dashboard-profile-${field.name}`}>
                    {field.value || <span className="sgk-proflist__empty">—</span>}
                  </dd>
                </div>
              ))}
            </dl>
            {listing.editable && (
              <div className="sgk-btnrow">
                <button
                  type="button"
                  className="sgk-btn sgk-btn--gold sgk-btn--sm"
                  onClick={() => setEditing(true)}
                  data-testid="partner-dashboard-edit-button"
                >
                  Edit details
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PartnerLayout>
  );
};

export default PartnerDashboard;
